import type { PrimaryStatKey } from '../sim/primary-stats.js';
import { resolveSourceProfile } from '../sim/source-profile.js';
import { WEAPON_PRIMARY_PROFILE_V0 } from '../sim/weapon-primary-profile.js';
import type { BaseStats } from '../sim/stats.js';
import type { WeaponFamily } from '../types.js';
import type { ReferenceProgressionBand } from './types.js';
import {
  combatSourceAtLevel,
  completePrimaryStats,
  type PrimaryAllocation,
  type PrimaryStatV0WeaponId,
  type ReferenceWeaponLevel,
} from './weapon-progression-v0.js';

export const PRIMARY_STAT_V0_VERSION = 'primary-stat-v0-face-value-2026-10-12' as const;

/**
 * Compatibility exports are references to ACTIVE.1's single authority, not
 * copied matrices. New consumers should normally use WEAPON_PRIMARY_PROFILE_V0
 * directly.
 */
export const PRIMARY_STAT_V0_DAMAGE_AFFINITY = WEAPON_PRIMARY_PROFILE_V0.damageAffinity;
export const PRIMARY_STAT_V0_HANDLING_AFFINITY = WEAPON_PRIMARY_PROFILE_V0.handlingAffinity;

/**
 * The V0 derived-stat coefficients. The Weapon×Primary damage/handling slopes
 * are references to ACTIVE.1 authority so changing that authority cannot
 * silently split runtime and Balance Lab.
 *
 * Every coefficient is per raw invested point: no common transform sits
 * between a stat's points and its effect, and a limit an effect needs is
 * stated on that effect.
 * - HP: `112 + 8L` base plus a flat amount per VIT point.
 * - Mana: `60 + 4L` pool and `1.2 + 0.177L` regen, each multiplied by
 *   `1 + slope * points` (MAG for the pool, WIL for regen).
 * - Move speed: linear in AGI, x3.00 at 500 points, no cap.
 * - Critical chance (TEC) and WIL resistance: straight lines that reach 1 at
 *   500 points and are not capped as numbers.
 * - Natural HP regen is capacity / a VIT-dependent recovery time: 125 s at
 *   zero VIT at every level, ~75 s at VIT 200, approaching 60 s.
 */
export const PRIMARY_STAT_V0_NUMERIC = Object.freeze({
  damageSlopePerWeightedPoint: WEAPON_PRIMARY_PROFILE_V0.damageSlope,
  handlingSlopePerWeightedPoint: WEAPON_PRIMARY_PROFILE_V0.attackRateSlope,
  moveSpeedPerAgiPoint: 2 / 500, // AGI 500 -> exactly x3.00, no cap
  baseHpFlat: 112,
  baseHpPerLevel: 8,
  hpPerVitPoint: 8,
  hpRecoveryTimeFloorS: 60,
  hpRecoveryTimeExtraS: 65,
  hpRecoveryTimeVitDecayPerPoint: 0.00731, // VIT 200 -> 75.07 s
  baseManaFlat: 60,
  baseManaPerLevel: 4,
  baseManaRegenFlatPerS: 1.2,
  baseManaRegenPerLevelPerS: 0.177,
  // Mana keeps the MULTIPLICATIVE stat form while HP is additive. The split is
  // not an inconsistency: `base x (1 + s*p)` == `base + (s*base(L))*p`, i.e.
  // the per-point value rises with level, which is what the canonical
  // CATALYST build needs because MAG is its 3L main stat. A flat per-point
  // constant loses base-proportionality and overshoots badly at low level.
  // VIT has no such problem: it is never a canonical 3:1:1 main stat.
  magMaxManaSlopePerPoint: 0.0085,
  wilManaRegenSlopePerPoint: 0.023,
  // Straight lines from 0 at 0 points to 1 at this many points. Neither value
  // is capped here: they keep rising past 1 as numbers.
  critChanceFullAtTecPoints: 500,
  wilResistFullAtWilPoints: 500,
  baseCritDamage: 0.5,
  critDamagePerPoint: 0.007, // 3.5 / 500: 500 points of the stat that owns a family's crit damage give +350% over the base
});

interface PrimaryStatV0MotionOwnership {
  windup: readonly PrimaryStatKey[];
  active: readonly PrimaryStatKey[];
  recovery: readonly PrimaryStatKey[];
  projectile: readonly PrimaryStatKey[];
}

function motionOwnership(
  windup: readonly PrimaryStatKey[],
  active: readonly PrimaryStatKey[],
  recovery: readonly PrimaryStatKey[],
  projectile: readonly PrimaryStatKey[],
): Readonly<PrimaryStatV0MotionOwnership> {
  return Object.freeze({
    windup: Object.freeze([...windup]),
    active: Object.freeze([...active]),
    recovery: Object.freeze([...recovery]),
    projectile: Object.freeze([...projectile]),
  });
}

/**
 * Semantic motion ownership is locked; exact per-phase conversion coefficients
 * remain deferred. V0 Balance Lab preserves the current cast:wait ratio while
 * applying the source-owned total handling rate.
 */
export const PRIMARY_STAT_V0_MOTION_OWNERSHIP: Readonly<Record<WeaponFamily, Readonly<PrimaryStatV0MotionOwnership>>> = Object.freeze({
  HEAVY: motionOwnership(['STR', 'TEC'], ['STR', 'TEC', 'AGI'], ['VIT', 'AGI'], []),
  BALANCED: motionOwnership(['AGI', 'TEC'], ['AGI', 'TEC'], ['AGI', 'TEC'], []),
  LIGHT: motionOwnership(['AGI', 'TEC'], ['AGI', 'TEC'], ['AGI'], []),
  POLEARM: motionOwnership(['TEC', 'STR'], ['TEC', 'AGI'], ['AGI'], []),
  RANGED: motionOwnership(['TEC', 'AGI', 'STR'], ['AGI', 'TEC'], ['AGI', 'TEC', 'STR'], ['TEC', 'STR']),
  CATALYST: motionOwnership(['AGI', 'TEC', 'MAG'], ['TEC', 'AGI'], ['AGI', 'WIL'], ['TEC', 'MAG']),
});

export const PRIMARY_STAT_V0_ROLE_SUMMARY: Readonly<Record<PrimaryStatKey, readonly string[]>> = Object.freeze({
  STR: Object.freeze(['weapon damage by family affinity', 'heavy/draw handling', 'impact/stagger', 'encumbrance compensation', 'bow projectile/draw force', 'future firearm recoil control']),
  AGI: Object.freeze(['movement speed', 'windup/active/recovery speed', 'fast-caster route', 'mobile weapon handling']),
  TEC: Object.freeze(['precision/technical execution', 'weapon handling', 'projectile speed/control', 'aim/thrust/channel execution', 'future critical/weak-point linkage']),
  VIT: Object.freeze(['max HP', 'HP regeneration', 'stagger/knockback resistance', 'encumbrance compensation', 'heavy handling']),
  MAG: Object.freeze(['magical output', 'max mana', 'catalyst handling', 'magical projectile potency/control']),
  WIL: Object.freeze(['mana regeneration', 'channel stability', 'interrupt/CC resistance', 'sustained catalyst execution']),
});

export interface PrimaryStatV0WeaponOutput {
  weaponId: PrimaryStatV0WeaponId;
  family: WeaponFamily;
  level: ReferenceWeaponLevel;
  allocation: PrimaryAllocation;
  rawDamageAtLevel: number;
  hitDamage: number;
  rawAttackRatePerS: number;
  attackRateEquivalentPerS: number;
  nominalSingleTargetDps: number;
}

export interface PrimaryStatV0DerivedStats {
  level: number;
  moveSpeedMultiplier: number;
  /** Absolute max HP (`112 + 8L` base, VIT contributes a flat amount per
   * point — not a multiplier). */
  maxHp: number;
  /** Absolute HP per second: maxHp / recoveryTime(VIT), independent of level at fixed VIT as a fraction of HP. */
  hpRegenPerS: number;
  maxMana: number;
  manaRegenPerS: number;
  /** WIL-driven, linear, 1 at `wilResistFullAtWilPoints`; not capped. The
   * stun/silence cast-save probability reads this value. */
  wilResist: number;
  /** CC duration multiplier `max(0, 1 - wilResist)`. Resistance past 1 is
   * full immunity, never a negative duration. */
  ccSusceptibility: number;
  /** TEC-driven, linear, 1 at `critChanceFullAtTecPoints`; not capped. The
   * combat stat aggregation clamps the summed chance to [0, 1]. */
  critChance: number;
  /** Common baseline bonus; growth is carried by the class map: half the class stat (STR physical, MAG magical), half the equipped weapon family's main stat. */
  critDamage: number;
  critDamageByClass: Readonly<{ PHYSICAL: number; MAGICAL: number }>;
  deferred: Readonly<{
    armorEfficiency: 'NO_DIRECT_VIT_MULTIPLIER';
    staggerKnockbackCoefficients: 'CALIBRATE_WITH_COMBAT';
    projectileSpeedCoefficient: 'CALIBRATE_WITH_PROJECTILE_RUNTIME';
    phaseSpecificSpeedCoefficients: 'CALIBRATE_WITH_ACTIVE_PHASE_RUNTIME';
  }>;
}

/**
 * BOOTSTRAP STAND-IN, superseded for `primaryStatV0DerivedStats()` callers by
 * TEC-driven critical probability and class-specific STR/MAG critical damage. Not a
 * calibrated balance table — these three numbers per band exist only so R.1's
 * reference players have *some* globalDamage/critChance/critDamage to
 * multiply through. Sole remaining reader: burst measurement's probe
 * scenario (`burst/measure.ts`'s `caster.baseStats`).
 */
export const REFERENCE_BAND_COMBAT_STATS_V0: Readonly<Record<ReferenceProgressionBand, BaseStats>> = Object.freeze({
  novice: { globalDamage: 0, critChance: 0.05, critDamage: 0.5 },
  intermediate: { globalDamage: 0.35, critChance: 0.15, critDamage: 0.75 },
  advanced: { globalDamage: 0.9, critChance: 0.3, critDamage: 1.0 },
});

/**
 * Current weapon conversion is an adapter over ACTIVE.1's production source
 * resolver. It never reimplements affinity weighting or damage/rate slopes.
 */
export function primaryStatV0WeaponOutput(
  weaponId: PrimaryStatV0WeaponId,
  level: ReferenceWeaponLevel,
  allocation: PrimaryAllocation,
): PrimaryStatV0WeaponOutput {
  const source = combatSourceAtLevel(weaponId, level);
  const resolved = resolveSourceProfile(source, completePrimaryStats(allocation), WEAPON_PRIMARY_PROFILE_V0);

  return Object.freeze({
    weaponId,
    family: source.family,
    level,
    allocation,
    rawDamageAtLevel: source.baseDamage,
    hitDamage: resolved.hitDamage,
    rawAttackRatePerS: source.baseAttackRate,
    attackRateEquivalentPerS: resolved.attackRatePerS,
    nominalSingleTargetDps: resolved.hitDamage * resolved.attackRatePerS,
  });
}

export function primaryStatV0BaseMana(level: number): number {
  return PRIMARY_STAT_V0_NUMERIC.baseManaFlat + PRIMARY_STAT_V0_NUMERIC.baseManaPerLevel * Math.max(0, level);
}

export function primaryStatV0BaseManaRegenPerS(level: number): number {
  return PRIMARY_STAT_V0_NUMERIC.baseManaRegenFlatPerS + PRIMARY_STAT_V0_NUMERIC.baseManaRegenPerLevelPerS * Math.max(0, level);
}

/** Mirrors `primaryStatV0BaseMana` — the mana-shaped HP base curve (§2.1). */
export function primaryStatV0BaseHp(level: number): number {
  return PRIMARY_STAT_V0_NUMERIC.baseHpFlat + PRIMARY_STAT_V0_NUMERIC.baseHpPerLevel * Math.max(0, level);
}

/** Natural HP regen of a zero-VIT player: level raises capacity, not recovery speed. */
export function primaryStatV0BaseHpRegenPerS(level: number): number {
  return primaryStatV0BaseHp(level) / (PRIMARY_STAT_V0_NUMERIC.hpRecoveryTimeFloorS + PRIMARY_STAT_V0_NUMERIC.hpRecoveryTimeExtraS);
}

export function primaryStatV0DerivedStats(level: number, allocation: PrimaryAllocation, weaponFamily: WeaponFamily): PrimaryStatV0DerivedStats {
  // Invested points count at face value: every effect below is a function of
  // the raw point count, with its own limit where it needs one.
  const agi = allocation.AGI ?? 0;
  const vit = allocation.VIT ?? 0;
  const mag = allocation.MAG ?? 0;
  const wil = allocation.WIL ?? 0;
  const tec = allocation.TEC ?? 0;
  const str = allocation.STR ?? 0;
  const maxHp = primaryStatV0BaseHp(level) + PRIMARY_STAT_V0_NUMERIC.hpPerVitPoint * vit;
  const hpRecoveryTimeS = PRIMARY_STAT_V0_NUMERIC.hpRecoveryTimeFloorS
    + PRIMARY_STAT_V0_NUMERIC.hpRecoveryTimeExtraS * Math.exp(-PRIMARY_STAT_V0_NUMERIC.hpRecoveryTimeVitDecayPerPoint * vit);
  const wilResist = wil / PRIMARY_STAT_V0_NUMERIC.wilResistFullAtWilPoints;
  const weaponMainStat = WEAPON_PRIMARY_PROFILE_V0.critDamageMainStat[weaponFamily];
  if (weaponMainStat === undefined) throw new Error(`primaryStatV0DerivedStats needs a known weapon family (got ${String(weaponFamily)})`);
  const weaponMain = allocation[weaponMainStat] ?? 0;
  return Object.freeze({
    level,
    moveSpeedMultiplier: 1 + PRIMARY_STAT_V0_NUMERIC.moveSpeedPerAgiPoint * agi,
    maxHp,
    hpRegenPerS: maxHp / hpRecoveryTimeS,
    maxMana: primaryStatV0BaseMana(level) * (1 + PRIMARY_STAT_V0_NUMERIC.magMaxManaSlopePerPoint * mag),
    manaRegenPerS: primaryStatV0BaseManaRegenPerS(level) * (1 + PRIMARY_STAT_V0_NUMERIC.wilManaRegenSlopePerPoint * wil),
    wilResist,
    ccSusceptibility: Math.max(0, 1 - wilResist),
    critChance: tec / PRIMARY_STAT_V0_NUMERIC.critChanceFullAtTecPoints,
    critDamage: PRIMARY_STAT_V0_NUMERIC.baseCritDamage,
    critDamageByClass: Object.freeze({
      PHYSICAL: PRIMARY_STAT_V0_NUMERIC.critDamagePerPoint * (0.5 * str + 0.5 * weaponMain),
      MAGICAL: PRIMARY_STAT_V0_NUMERIC.critDamagePerPoint * (0.5 * mag + 0.5 * weaponMain),
    }),
    deferred: Object.freeze({
      armorEfficiency: 'NO_DIRECT_VIT_MULTIPLIER',
      staggerKnockbackCoefficients: 'CALIBRATE_WITH_COMBAT',
      projectileSpeedCoefficient: 'CALIBRATE_WITH_PROJECTILE_RUNTIME',
      phaseSpecificSpeedCoefficients: 'CALIBRATE_WITH_ACTIVE_PHASE_RUNTIME',
    }),
  });
}

export type { PrimaryAllocation, PrimaryStatV0WeaponId, ReferenceWeaponLevel } from './weapon-progression-v0.js';
